/**
 * Vespa filter field registry and constants.
 *
 * Uses PQueryBuilder's standard types directly — no custom enum or condition
 * shape. Vespa's filterable fields are expressed as a PQueryBuilderField[]
 * and conditions are standard QueryCondition from @pushly/pquery.
 */

import { ConditionOperatorType } from '@pushly/pquery'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'

/**
 * Vespa document fields available for filtering.
 *
 * Published date is deliberately not filterable here — it's handled
 * exclusively by the Content Window control ("Published in the last...")
 * to avoid two overlapping date controls with unclear precedence.
 */
export enum VespaFilterField {
    KEYWORDS = 'keywords',
    TITLE = 'title',
    DESCRIPTION = 'description',
}

/**
 * Vespa's filterable fields expressed as PQueryBuilderField[].
 *
 * This is the field registry passed to ConditionEditorForm — it determines
 * which fields appear in the Field dropdown, which operators are valid per
 * field, and which value input component renders for each field type.
 */
export const VESPA_FILTER_FIELDS: PQueryBuilderField[] = [
    {
        key: VespaFilterField.KEYWORDS,
        label: 'Keywords',
        type: 'array',
        operators: [
            ConditionOperatorType.EQUALS_ANY,
            ConditionOperatorType.ARRAY_CONTAINS,
            ConditionOperatorType.NOT_EQUALS_ANY,
            ConditionOperatorType.ARRAY_NOT_CONTAINS,
        ],
    },
    {
        key: VespaFilterField.TITLE,
        label: 'Title',
        type: 'text',
        operators: [
            ConditionOperatorType.EQUALS,
            ConditionOperatorType.NOT_EQUALS,
            ConditionOperatorType.CONTAINS,
            ConditionOperatorType.NOT_CONTAINS,
        ],
    },
    {
        key: VespaFilterField.DESCRIPTION,
        label: 'Description',
        type: 'text',
        operators: [
            ConditionOperatorType.EQUALS,
            ConditionOperatorType.NOT_EQUALS,
            ConditionOperatorType.CONTAINS,
            ConditionOperatorType.NOT_CONTAINS,
        ],
    },
]

/** Inclusion operators — conditions using these match content TO recommend. */
export const INCLUSION_OPERATORS = new Set([
    ConditionOperatorType.EQUALS,
    ConditionOperatorType.EQUALS_ANY,
    ConditionOperatorType.ARRAY_CONTAINS,
    ConditionOperatorType.CONTAINS,
    ConditionOperatorType.GTE,
    ConditionOperatorType.LTE,
])

/** Exclusion operators — conditions using these EXCLUDE content. */
export const EXCLUSION_OPERATORS = new Set([
    ConditionOperatorType.NOT_EQUALS,
    ConditionOperatorType.NOT_EQUALS_ANY,
    ConditionOperatorType.ARRAY_NOT_CONTAINS,
    ConditionOperatorType.NOT_CONTAINS,
])

/**
 * Maximum number of filter conditions per configuration.
 *
 * Previously enforced as 10 per mode (include/exclude) across two separate
 * chip lists — 20 combined. Now that both modes share a single flat
 * PQueryBuilder condition list, this is the combined cap (unchanged from
 * the old total), enforced in vespa-configuration-editor.tsx's
 * handlePQueryBuilderCommit since PQueryBuilder has no built-in max-conditions
 * constraint.
 */
export const MAX_VESPA_FILTERS = 20
